import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  LayoutDashboard, Mic2, CalendarDays, BookOpen, ImageIcon, HeartHandshake,
  Mail, Phone, Plus, Trash2, Save, ExternalLink, Download,
  Search, Eye, EyeOff, ArrowLeft, Pencil, X, Droplets, Home, Clock, Sparkles, UserRound
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ baseURL: API });

const tabs = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "homepage", label: "Homepage Text", icon: Home },
  { id: "servicetimes", label: "Service Times", icon: Clock },
  { id: "sermons", label: "Sermons", icon: Mic2 },
  { id: "events", label: "Events", icon: CalendarDays },
  { id: "ministries", label: "Ministries", icon: Sparkles },
  { id: "staff", label: "Meet Our Staff", icon: UserRound },
  { id: "verse", label: "Verse", icon: BookOpen },
  { id: "gallery", label: "Gallery", icon: ImageIcon },
  { id: "prayers", label: "Prayers", icon: HeartHandshake },
  { id: "subscribers", label: "Subscribers", icon: Mail },
  { id: "contact", label: "Contact Info", icon: Phone },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const { toast } = useToast();

  return (
    <div className="min-h-screen bg-[#faf7f2] grain-bg">
      <header className="bg-[#0a2d38] text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-[#c8a36a] hover:text-[#f0d9a8] inline-flex items-center gap-2 text-sm">
              <ArrowLeft className="h-4 w-4" /> View site
            </Link>
            <span className="text-white/30">|</span>
            <span className="font-serif-display text-xl">Pastor Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <img src="/brand/logo-light-192.png" alt="Living Waters" className="h-10 w-10 object-contain rounded-full" />
            <span className="font-serif-display text-sm hidden sm:inline">Living Waters</span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-[240px_1fr] gap-8">
        <aside className="space-y-1">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-left text-sm font-medium transition-colors ${
                  active ? "bg-[#0e5469] text-white" : "text-[#3b4d54] hover:bg-white"
                }`}
              >
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </aside>

        <main>
          {tab === "overview" && <Overview />}
          {tab === "homepage" && <HomepageAdmin toast={toast} />}
          {tab === "servicetimes" && <ServiceTimesAdmin toast={toast} />}
          {tab === "sermons" && <SermonsAdmin toast={toast} />}
          {tab === "events" && <EventsAdmin toast={toast} />}
          {tab === "ministries" && <MinistriesAdmin toast={toast} />}
          {tab === "staff" && <StaffAdmin toast={toast} />}
          {tab === "verse" && <VerseAdmin toast={toast} />}
          {tab === "gallery" && <GalleryAdmin toast={toast} />}
          {tab === "prayers" && <PrayersAdmin toast={toast} />}
          {tab === "subscribers" && <SubscribersAdmin toast={toast} />}
          {tab === "contact" && <ContactAdmin toast={toast} />}
        </main>
      </div>
    </div>
  );
}

function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 pb-5 border-b border-[#e7dfd0]">
      <div>
        <h1 className="font-serif-display text-3xl text-[#0a2d38]">{title}</h1>
        {subtitle && <p className="text-[#5b6c72] mt-1">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

function Card({ children, className = "" }) {
  return <div className={`bg-white rounded-md border border-[#e7dfd0] p-6 ${className}`}>{children}</div>;
}

function Overview() {
  const [stats, setStats] = useState({ sermons: "—", events: "—", prayers: "—", subs: "—" });
  useEffect(() => {
    (async () => {
      try {
        const [s, e, p, su] = await Promise.all([
          ax.get("/admin/sermons"), ax.get("/admin/events"),
          ax.get("/admin/prayers"), ax.get("/admin/subscribers"),
        ]);
        setStats({ sermons: s.data.length, events: e.data.length, prayers: p.data.length, subs: su.data.length });
      } catch (err) {}
    })();
  }, []);
  const items = [
    { label: "Sermons", value: stats.sermons, icon: Mic2 },
    { label: "Upcoming Events", value: stats.events, icon: CalendarDays },
    { label: "Prayer Requests", value: stats.prayers, icon: HeartHandshake },
    { label: "Newsletter Subs", value: stats.subs, icon: Mail },
  ];
  return (
    <div>
      <PageHeader title="Welcome back" subtitle="Here's what's happening with Living Waters." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((it, i) => {
          const Icon = it.icon;
          return (
            <Card key={i}>
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-[0.22em] text-[#7a8a8f] font-semibold">{it.label}</div>
                <Icon className="h-4 w-4 text-[#c8a36a]" />
              </div>
              <div className="mt-3 font-serif-display text-4xl text-[#0a2d38]">{it.value}</div>
            </Card>
          );
        })}
      </div>
      <Card className="mt-6">
        <h3 className="font-serif-display text-xl text-[#0a2d38] mb-3">Quick tips</h3>
        <ul className="text-sm text-[#3b4d54] space-y-2 list-disc pl-5">
          <li>To add a new sermon: paste a YouTube link → grab the ID after <code className="bg-[#f3ede0] px-1.5 rounded">v=</code> → fill the form.</li>
          <li>Prayer requests are confidential. Mark sensitive ones private in the form on the public site.</li>
          <li>Use the Team tab to add or remove staff who can sign in here.</li>
        </ul>
      </Card>
    </div>
  );
}

/* --------- Sermons --------- */
function SermonsAdmin({ toast }) {
  const empty = { title: "", speaker: "Pastor Hershel Clay", date: "", youtubeId: "", series: "", sort_order: 0 };
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const r = await ax.get("/admin/sermons");
    setList(r.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.youtubeId) { toast({ title: "Title and YouTube ID required" }); return; }
    const ytId = extractYouTubeId(form.youtubeId);
    const payload = { ...form, youtubeId: ytId };
    try {
      if (editing) await ax.put(`/admin/sermons/${editing}`, payload);
      else await ax.post("/admin/sermons", payload);
      toast({ title: editing ? "Sermon updated" : "Sermon added" });
      setForm(empty); setEditing(null); load();
    } catch (err) { toast({ title: "Save failed" }); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this sermon?")) return;
    await ax.delete(`/admin/sermons/${id}`); load();
  };
  const edit = (s) => { setEditing(s.id); setForm({ title: s.title, speaker: s.speaker, date: s.date, youtubeId: s.youtubeId, series: s.series, sort_order: s.sort_order }); };
  const cancel = () => { setEditing(null); setForm(empty); };

  return (
    <div>
      <PageHeader title="Sermons" subtitle="Add, edit, and reorder sermons displayed on the website." />
      <Card>
        <h3 className="font-serif-display text-xl text-[#0a2d38] mb-4 flex items-center gap-2">
          {editing ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {editing ? "Edit sermon" : "Add new sermon"}
        </h3>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Speaker</Label><Input value={form.speaker} onChange={(e) => setForm({ ...form, speaker: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Date (e.g. July 7, 2025)</Label><Input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
          <div><Label>YouTube link or ID</Label><Input value={form.youtubeId} onChange={(e) => setForm({ ...form, youtubeId: e.target.value })} placeholder="https://youtube.com/watch?v=..." className="mt-1.5" /></div>
          <div><Label>Series</Label><Input value={form.series} onChange={(e) => setForm({ ...form, series: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Sort order (higher = top)</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || "0") })} className="mt-1.5" /></div>
          <div className="sm:col-span-2 flex gap-2">
            <button type="submit" className="btn-teal rounded-full px-6 py-2.5 font-semibold inline-flex items-center gap-2"><Save className="h-4 w-4" />{editing ? "Save changes" : "Add sermon"}</button>
            {editing && <button type="button" onClick={cancel} className="rounded-full px-6 py-2.5 border border-[#e7dfd0] text-[#3b4d54]">Cancel</button>}
          </div>
        </form>
      </Card>

      <div className="mt-6 space-y-3">
        {list.map((s) => (
          <Card key={s.id} className="flex items-center gap-4">
            <img src={`https://i.ytimg.com/vi/${s.youtubeId}/hqdefault.jpg`} alt="" className="h-16 w-28 object-cover rounded-sm flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-serif-display text-lg text-[#0a2d38] truncate">{s.title}</div>
              <div className="text-xs text-[#7a8a8f]">{s.series} · {s.speaker} · {s.date}</div>
            </div>
            <a href={`https://youtu.be/${s.youtubeId}`} target="_blank" rel="noreferrer" className="text-[#0e5469] hover:text-[#c8a36a]" title="Open on YouTube"><ExternalLink className="h-4 w-4" /></a>
            <button onClick={() => edit(s)} className="text-[#0e5469] hover:text-[#c8a36a]" title="Edit"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => del(s.id)} className="text-red-600 hover:text-red-800" title="Delete"><Trash2 className="h-4 w-4" /></button>
          </Card>
        ))}
        {list.length === 0 && <div className="text-center py-10 text-[#7a8a8f]">No sermons yet. Add your first one above.</div>}
      </div>
    </div>
  );
}

function extractYouTubeId(input) {
  if (!input) return "";
  const m1 = input.match(/[?&]v=([\w-]{6,})/);
  if (m1) return m1[1];
  const m2 = input.match(/youtu\.be\/([\w-]{6,})/);
  if (m2) return m2[1];
  const m3 = input.match(/embed\/([\w-]{6,})/);
  if (m3) return m3[1];
  return input.trim();
}

/* --------- Events --------- */
function EventsAdmin({ toast }) {
  const empty = { title: "", date: "", time: "", location: "", sort_order: 0 };
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const load = async () => setList((await ax.get("/admin/events")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.date || !form.time) { toast({ title: "Title, date, and time are required" }); return; }
    if (editing) await ax.put(`/admin/events/${editing}`, form);
    else await ax.post("/admin/events", form);
    toast({ title: editing ? "Event updated" : "Event added" });
    setForm(empty); setEditing(null); load();
  };
  const del = async (id) => { if (window.confirm("Delete this event?")) { await ax.delete(`/admin/events/${id}`); load(); } };
  const edit = (e) => { setEditing(e.id); setForm({ title: e.title, date: e.date, time: e.time, location: e.location, sort_order: e.sort_order }); };

  return (
    <div>
      <PageHeader title="Events" subtitle="Manage upcoming events that show up on the homepage." />
      <Card>
        <h3 className="font-serif-display text-xl text-[#0a2d38] mb-4">{editing ? "Edit event" : "Add event"}</h3>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Date (e.g. August 9 or Every Sunday)</Label><Input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Time (e.g. 7:00 PM)</Label><Input value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Sort order</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || "0") })} className="mt-1.5" /></div>
          <div className="sm:col-span-2 flex gap-2">
            <button type="submit" className="btn-teal rounded-full px-6 py-2.5 font-semibold inline-flex items-center gap-2"><Save className="h-4 w-4" />{editing ? "Save" : "Add"}</button>
            {editing && <button type="button" onClick={() => { setEditing(null); setForm(empty); }} className="rounded-full px-6 py-2.5 border border-[#e7dfd0]">Cancel</button>}
          </div>
        </form>
      </Card>

      <div className="mt-6 space-y-3">
        {list.map((e) => (
          <Card key={e.id} className="flex items-center justify-between gap-4">
            <div>
              <div className="font-serif-display text-lg text-[#0a2d38]">{e.title}</div>
              <div className="text-xs text-[#7a8a8f]">{e.date} · {e.time} · {e.location}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => edit(e)} className="text-[#0e5469] hover:text-[#c8a36a]"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => del(e.id)} className="text-red-600 hover:text-red-800"><Trash2 className="h-4 w-4" /></button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* --------- Verse --------- */
function VerseAdmin({ toast }) {
  const [v, setV] = useState({ text: "", reference: "" });
  useEffect(() => {
    ax.get("/verse").then(r => setV({ text: r.data.text, reference: r.data.reference }));
  }, []);
  const save = async (e) => {
    e.preventDefault();
    await ax.put("/admin/verse", v);
    toast({ title: "Verse updated" });
  };
  return (
    <div>
      <PageHeader title="Verse of the Week" subtitle="Highlighted on the homepage About section." />
      <Card>
        <form onSubmit={save} className="space-y-4">
          <div><Label>Verse text</Label><Textarea rows={4} value={v.text} onChange={(e) => setV({ ...v, text: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Reference (e.g. John 4:14)</Label><Input value={v.reference} onChange={(e) => setV({ ...v, reference: e.target.value })} className="mt-1.5" /></div>
          <button type="submit" className="btn-teal rounded-full px-6 py-2.5 font-semibold inline-flex items-center gap-2"><Save className="h-4 w-4" />Save verse</button>
        </form>
      </Card>
    </div>
  );
}

/* --------- Gallery --------- */
function GalleryAdmin({ toast }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ url: "", caption: "", sort_order: 0 });
  const load = async () => setList((await ax.get("/admin/gallery")).data);
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!form.url) return;
    await ax.post("/admin/gallery", form);
    setForm({ url: "", caption: "", sort_order: 0 }); load();
    toast({ title: "Image added" });
  };
  const del = async (id) => { if (window.confirm("Remove this image?")) { await ax.delete(`/admin/gallery/${id}`); load(); } };
  return (
    <div>
      <PageHeader title="Gallery" subtitle="Photos on the 'Moments of Faith' section." />
      <Card>
        <form onSubmit={add} className="grid sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
          <div><Label>Image URL</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="mt-1.5" placeholder="https://..." /></div>
          <div><Label>Order</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || "0") })} className="mt-1.5 w-24" /></div>
          <button type="submit" className="btn-teal rounded-full px-5 h-10 font-semibold inline-flex items-center gap-2"><Plus className="h-4 w-4" />Add</button>
        </form>
      </Card>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        {list.map((g) => (
          <div key={g.id} className="relative group rounded-md overflow-hidden border border-[#e7dfd0] aspect-square">
            <img src={g.url} alt="" className="h-full w-full object-cover" />
            <button onClick={() => del(g.id)} className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/95 text-red-600 hover:bg-red-600 hover:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* --------- Prayers --------- */
function PrayersAdmin({ toast }) {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [showPrivate, setShowPrivate] = useState(true);
  const load = async () => setList((await ax.get("/admin/prayers")).data);
  useEffect(() => { load(); }, []);
  const filtered = list.filter(p => {
    if (!showPrivate && p.is_private) return false;
    if (!q) return true;
    const t = (p.name + " " + p.email + " " + p.subject + " " + p.message).toLowerCase();
    return t.includes(q.toLowerCase());
  });
  const del = async (id) => { if (window.confirm("Delete this prayer?")) { await ax.delete(`/admin/prayers/${id}`); load(); } };
  return (
    <div>
      <PageHeader title="Prayer Requests" subtitle={`${list.length} total · ${list.filter(p => p.is_private).length} private`} />
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7a8a8f]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, or text" className="pl-10" />
        </div>
        <button onClick={() => setShowPrivate(!showPrivate)} className="px-4 rounded-md border border-[#e7dfd0] bg-white text-sm inline-flex items-center gap-2">
          {showPrivate ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          {showPrivate ? "Hide private" : "Show private"}
        </button>
      </div>
      <div className="space-y-3">
        {filtered.map(p => (
          <Card key={p.id}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-serif-display text-lg text-[#0a2d38]">{p.name}</span>
                  {p.is_private && <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">Private</span>}
                  <a href={`mailto:${p.email}`} className="text-xs text-[#0e5469] hover:text-[#c8a36a]">{p.email}</a>
                </div>
                {p.subject && <div className="text-xs uppercase tracking-[0.22em] text-[#c8a36a] mt-1.5 font-semibold">{p.subject}</div>}
                <p className="mt-2 text-[#3b4d54] whitespace-pre-wrap">{p.message}</p>
                <div className="text-[11px] text-[#7a8a8f] mt-3">{new Date(p.created_at).toLocaleString()}</div>
              </div>
              <button onClick={() => del(p.id)} className="text-red-600 hover:text-red-800"><Trash2 className="h-4 w-4" /></button>
            </div>
          </Card>
        ))}
        {filtered.length === 0 && <div className="text-center py-10 text-[#7a8a8f]">No prayer requests {q ? "match your search" : "yet"}.</div>}
      </div>
    </div>
  );
}

/* --------- Subscribers --------- */
function SubscribersAdmin({ toast }) {
  const [list, setList] = useState([]);
  useEffect(() => { ax.get("/admin/subscribers").then(r => setList(r.data)); }, []);
  const exportCsv = () => {
    window.open(`${API}/admin/subscribers.csv`, "_blank");
  };
  return (
    <div>
      <PageHeader title="Newsletter Subscribers"
        subtitle={`${list.length} people on your list.`}
        action={<button onClick={exportCsv} className="btn-gold rounded-full px-5 py-2.5 font-semibold inline-flex items-center gap-2"><Download className="h-4 w-4" />Export CSV</button>}
      />
      <Card>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[#7a8a8f] text-xs uppercase tracking-wider">
            <th className="py-2">Email</th><th className="py-2">Subscribed</th>
          </tr></thead>
          <tbody>
            {list.map(s => (
              <tr key={s.id} className="border-t border-[#e7dfd0]">
                <td className="py-2.5"><a className="text-[#0e5469] hover:text-[#c8a36a]" href={`mailto:${s.email}`}>{s.email}</a></td>
                <td className="py-2.5 text-[#7a8a8f]">{new Date(s.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan="2" className="text-center py-8 text-[#7a8a8f]">No subscribers yet.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}


/* --------- Contact info --------- */
function ContactAdmin({ toast }) {
  const [c, setC] = useState(null);
  useEffect(() => { ax.get("/contact-info").then(r => setC(r.data)); }, []);
  const save = async (e) => {
    e.preventDefault();
    await ax.put("/admin/contact-info", c);
    toast({ title: "Contact info updated" });
  };
  if (!c) return null;
  const fields = [
    ["pastors", "Pastors"], ["address", "Address"], ["phone", "Phone (display)"], ["phoneRaw", "Phone (digits only)"],
    ["email", "Email"], ["facebook", "Facebook URL"], ["youtube", "YouTube URL"], ["telegram", "Telegram URL"], ["donateUrl", "Donate URL"],
  ];
  return (
    <div>
      <PageHeader title="Contact Info" subtitle="Update what shows in the footer and Visit section." />
      <Card>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
          {fields.map(([k, label]) => (
            <div key={k} className={k === "address" ? "sm:col-span-2" : ""}>
              <Label>{label}</Label>
              <Input value={c[k] || ""} onChange={(e) => setC({ ...c, [k]: e.target.value })} className="mt-1.5" />
            </div>
          ))}
          <div className="sm:col-span-2">
            <button type="submit" className="btn-teal rounded-full px-6 py-2.5 font-semibold inline-flex items-center gap-2"><Save className="h-4 w-4" />Save</button>
          </div>
        </form>
      </Card>
    </div>
  );
}

/* --------- Service Times --------- */
function ServiceTimesAdmin({ toast }) {
  const empty = { day: "Sunday", name: "", time: "", note: "", sort_order: 0 };
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const load = async () => setList((await ax.get("/admin/service-times")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.day || !form.name || !form.time) { toast({ title: "Day, name, and time are required" }); return; }
    try {
      if (editing) await ax.put(`/admin/service-times/${editing}`, form);
      else await ax.post("/admin/service-times", form);
      toast({ title: editing ? "Service time updated" : "Service time added" });
      setForm(empty); setEditing(null); load();
    } catch (err) { toast({ title: "Save failed" }); }
  };
  const del = async (id) => { if (window.confirm("Delete this service time?")) { await ax.delete(`/admin/service-times/${id}`); load(); } };
  const edit = (s) => { setEditing(s.id); setForm({ day: s.day, name: s.name, time: s.time, note: s.note || "", sort_order: s.sort_order }); };

  return (
    <div>
      <PageHeader title="Service Times" subtitle="These show up in the blue Service Times band on the homepage." />
      <Card>
        <h3 className="font-serif-display text-xl text-[#0a2d38] mb-4">{editing ? "Edit service time" : "Add service time"}</h3>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          <div><Label>Day (e.g. Sunday, Wednesday)</Label><Input value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Service name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" placeholder="Worship Service" /></div>
          <div><Label>Time (e.g. 10:45 AM)</Label><Input value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Sort order</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || "0") })} className="mt-1.5" /></div>
          <div className="sm:col-span-2"><Label>Small note (optional, shows in italics)</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" placeholder="Children's Church too!" /></div>
          <div className="sm:col-span-2 flex gap-2">
            <button type="submit" className="btn-teal rounded-full px-6 py-2.5 font-semibold inline-flex items-center gap-2"><Save className="h-4 w-4" />{editing ? "Save" : "Add"}</button>
            {editing && <button type="button" onClick={() => { setEditing(null); setForm(empty); }} className="rounded-full px-6 py-2.5 border border-[#e7dfd0]">Cancel</button>}
          </div>
        </form>
      </Card>

      <div className="mt-6 space-y-3">
        {list.map((s) => (
          <Card key={s.id} className="flex items-center justify-between gap-4">
            <div>
              <div className="text-xs text-[#c8a36a] uppercase tracking-[0.28em] font-semibold">{s.day}</div>
              <div className="font-serif-display text-lg text-[#0a2d38]">{s.name} &mdash; {s.time}</div>
              {s.note && <div className="text-xs text-[#7a8a8f] italic mt-0.5">{s.note}</div>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => edit(s)} className="text-[#0e5469] hover:text-[#c8a36a]"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => del(s.id)} className="text-red-600 hover:text-red-800"><Trash2 className="h-4 w-4" /></button>
            </div>
          </Card>
        ))}
        {list.length === 0 && <div className="text-center py-10 text-[#7a8a8f]">No service times yet.</div>}
      </div>
    </div>
  );
}

/* --------- Homepage (hero + about text) --------- */
function HomepageAdmin({ toast }) {
  const [hp, setHp] = useState(null);
  useEffect(() => { ax.get("/homepage").then(r => setHp(r.data)); }, []);
  const save = async (e) => {
    e.preventDefault();
    try {
      await ax.put("/admin/homepage", hp);
      toast({ title: "Homepage content updated" });
    } catch (err) { toast({ title: "Save failed" }); }
  };
  if (!hp) return null;
  const field = (key, label, multiline = false, placeholder = "") => (
    <div key={key} className={multiline ? "sm:col-span-2" : ""}>
      <Label>{label}</Label>
      {multiline ? (
        <Textarea rows={3} value={hp[key] || ""} onChange={(e) => setHp({ ...hp, [key]: e.target.value })} className="mt-1.5" placeholder={placeholder} />
      ) : (
        <Input value={hp[key] || ""} onChange={(e) => setHp({ ...hp, [key]: e.target.value })} className="mt-1.5" placeholder={placeholder} />
      )}
    </div>
  );

  return (
    <div>
      <PageHeader title="Homepage Text" subtitle="Edit the big headlines and welcome text on the homepage." />
      <form onSubmit={save} className="space-y-5">
        <Card>
          <h3 className="font-serif-display text-xl text-[#0a2d38] mb-4">Hero (very top of page)</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {field("hero_eyebrow", "Tiny label above title", false, "Assemblies of God")}
            {field("hero_title_line1", "Title \u2013 line 1")}
            {field("hero_title_italic", "Title \u2013 gold italic middle line")}
            {field("hero_title_line3", "Title \u2013 line 3")}
            {field("hero_description", "Welcome paragraph", true)}
            {field("hero_primary_cta_label", "Main button label")}
            {field("hero_primary_cta_href", "Main button link")}
            {field("next_service_label", "Bottom-right label")}
            {field("next_service_value", "Bottom-right value", false, "Sunday \u00b7 10:45 AM")}
          </div>
        </Card>

        <Card>
          <h3 className="font-serif-display text-xl text-[#0a2d38] mb-4">About section</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {field("about_eyebrow", "Tiny label")}
            {field("about_title_part1", "Title \u2013 regular part")}
            {field("about_title_italic", "Title \u2013 gold italic part")}
            {field("about_paragraph1", "Paragraph 1", true)}
            {field("about_paragraph2", "Paragraph 2", true)}
            {field("about_stat1_value", "Stat 1 value")}
            {field("about_stat1_label", "Stat 1 label")}
            {field("about_stat2_value", "Stat 2 value")}
            {field("about_stat2_label", "Stat 2 label")}
            {field("about_stat3_value", "Stat 3 value")}
            {field("about_stat3_label", "Stat 3 label")}
          </div>
        </Card>

        <Card>
          <h3 className="font-serif-display text-xl text-[#0a2d38] mb-4">Ministries section heading</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {field("ministries_eyebrow", "Tiny label")}
            {field("ministries_title_part1", "Title \u2013 regular part")}
            {field("ministries_title_italic", "Title \u2013 gold italic part")}
            {field("ministries_subtitle", "Right-side paragraph", true)}
          </div>
        </Card>

        <button type="submit" className="btn-teal rounded-full px-8 py-3 font-semibold inline-flex items-center gap-2"><Save className="h-4 w-4" />Save all homepage changes</button>
      </form>
    </div>
  );
}

/* --------- Ministries --------- */
function MinistriesAdmin({ toast }) {
  const empty = {
    slug: "", title: "", desc: "", icon: "Heart", hero: "", tagline: "",
    verse_text: "", verse_reference: "", when: "", where: "", who_for: "",
    what_to_expect: "", dress_code: "", cta_label: "Learn More", cta_href: "/#visit", sort_order: 0,
  };
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const load = async () => setList((await ax.get("/admin/ministries")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.slug || !form.title) { toast({ title: "Slug and title are required" }); return; }
    const payload = {
      ...form,
      what_to_expect: typeof form.what_to_expect === "string"
        ? form.what_to_expect.split("\n").map(x => x.trim()).filter(Boolean)
        : form.what_to_expect,
      sort_order: parseInt(form.sort_order || 0),
    };
    try {
      if (editing) await ax.put(`/admin/ministries/${editing}`, payload);
      else await ax.post("/admin/ministries", payload);
      toast({ title: editing ? "Ministry updated" : "Ministry added" });
      setForm(empty); setEditing(null); load();
    } catch (err) {
      toast({ title: "Save failed", description: err?.response?.data?.detail || "" });
    }
  };
  const del = async (id) => { if (window.confirm("Delete this ministry page?")) { await ax.delete(`/admin/ministries/${id}`); load(); } };
  const edit = (m) => {
    setEditing(m.id);
    setForm({ ...m, what_to_expect: (m.what_to_expect || []).join("\n") });
  };

  return (
    <div>
      <PageHeader title="Ministries" subtitle="Edit the ministry cards and their welcome pages." />
      <Card>
        <h3 className="font-serif-display text-xl text-[#0a2d38] mb-4">{editing ? "Edit ministry" : "Add ministry"}</h3>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          <div><Label>Slug (URL id, lowercase-with-dashes)</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="mt-1.5" placeholder="youth-ministry" /></div>
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
          <div className="sm:col-span-2"><Label>Short description (shows on homepage card)</Label><Textarea rows={2} value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Icon name (Lucide)</Label><Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className="mt-1.5" placeholder="Sparkles, Heart, Church, Users, BookOpen, Baby, HeartHandshake" /></div>
          <div><Label>Sort order</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="mt-1.5" /></div>
          <div className="sm:col-span-2"><Label>Hero image URL</Label><Input value={form.hero} onChange={(e) => setForm({ ...form, hero: e.target.value })} className="mt-1.5" placeholder="https://..." /></div>
          <div className="sm:col-span-2"><Label>Tagline (script italic under hero title)</Label><Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="mt-1.5" /></div>
          <div className="sm:col-span-2"><Label>Scripture verse</Label><Textarea rows={2} value={form.verse_text} onChange={(e) => setForm({ ...form, verse_text: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Verse reference</Label><Input value={form.verse_reference} onChange={(e) => setForm({ ...form, verse_reference: e.target.value })} className="mt-1.5" placeholder="John 4:14" /></div>
          <div><Label>When</Label><Input value={form.when} onChange={(e) => setForm({ ...form, when: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Where</Label><Input value={form.where} onChange={(e) => setForm({ ...form, where: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Dress code (optional)</Label><Input value={form.dress_code} onChange={(e) => setForm({ ...form, dress_code: e.target.value })} className="mt-1.5" /></div>
          <div className="sm:col-span-2"><Label>Who it&rsquo;s for</Label><Textarea rows={2} value={form.who_for} onChange={(e) => setForm({ ...form, who_for: e.target.value })} className="mt-1.5" /></div>
          <div className="sm:col-span-2"><Label>What to expect (one bullet per line)</Label><Textarea rows={6} value={form.what_to_expect} onChange={(e) => setForm({ ...form, what_to_expect: e.target.value })} className="mt-1.5" placeholder="Heartfelt worship...&#10;Prayer for anyone...&#10;A Scripture-rooted message..." /></div>
          <div><Label>Call-to-action button label</Label><Input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} className="mt-1.5" /></div>
          <div><Label>CTA link</Label><Input value={form.cta_href} onChange={(e) => setForm({ ...form, cta_href: e.target.value })} className="mt-1.5" placeholder="/#visit or mailto:... or https://..." /></div>
          <div className="sm:col-span-2 flex gap-2">
            <button type="submit" className="btn-teal rounded-full px-6 py-2.5 font-semibold inline-flex items-center gap-2"><Save className="h-4 w-4" />{editing ? "Save changes" : "Add ministry"}</button>
            {editing && <button type="button" onClick={() => { setEditing(null); setForm(empty); }} className="rounded-full px-6 py-2.5 border border-[#e7dfd0]">Cancel</button>}
          </div>
        </form>
      </Card>

      <div className="mt-6 space-y-3">
        {list.map((m) => (
          <Card key={m.id} className="flex items-center gap-4">
            {m.hero && <img src={m.hero} alt="" className="h-16 w-24 object-cover rounded-sm flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="font-serif-display text-lg text-[#0a2d38]">{m.title}</div>
              <div className="text-xs text-[#7a8a8f]">/ministries/{m.slug}</div>
            </div>
            <a href={`/ministries/${m.slug}`} target="_blank" rel="noreferrer" className="text-[#0e5469] hover:text-[#c8a36a]" title="View page"><ExternalLink className="h-4 w-4" /></a>
            <button onClick={() => edit(m)} className="text-[#0e5469] hover:text-[#c8a36a]"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => del(m.id)} className="text-red-600 hover:text-red-800"><Trash2 className="h-4 w-4" /></button>
          </Card>
        ))}
      </div>
    </div>
  );
}


/* --------- Staff --------- */
function StaffAdmin({ toast }) {
  const empty = { name: "", role: "", bio: "", photo: "", email: "", phone: "", facebook: "", instagram: "", sort_order: 0 };
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const load = async () => setList((await ax.get("/admin/staff")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast({ title: "Name is required" }); return; }
    const payload = { ...form, sort_order: parseInt(form.sort_order || 0) };
    try {
      if (editing) await ax.put(`/admin/staff/${editing}`, payload);
      else await ax.post("/admin/staff", payload);
      toast({ title: editing ? "Staff member updated" : "Staff member added" });
      setForm(empty); setEditing(null); load();
    } catch (err) { toast({ title: "Save failed", description: err?.response?.data?.detail || "" }); }
  };
  const del = async (id) => { if (window.confirm("Remove this staff member?")) { await ax.delete(`/admin/staff/${id}`); load(); } };
  const edit = (s) => { setEditing(s.id); setForm({ ...empty, ...s }); };

  return (
    <div>
      <PageHeader title="Meet Our Staff" subtitle="Manage the pastors and leaders shown on the Staff & Leaders page." />

      <Card>
        <h3 className="font-serif-display text-xl text-[#0a2d38] mb-4">{editing ? "Edit staff member" : "Add staff member"}</h3>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          <div><Label>Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" placeholder="Pastor Hershel Clay" /></div>
          <div><Label>Role / title</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1.5" placeholder="Lead Pastor" /></div>
          <div className="sm:col-span-2"><Label>Photo URL</Label><Input value={form.photo} onChange={(e) => setForm({ ...form, photo: e.target.value })} className="mt-1.5" placeholder="https://..." /></div>
          <div className="sm:col-span-2"><Label>Short bio</Label><Textarea rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1.5" placeholder="A few sentences about their role, heart for the church, family, etc." /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Facebook URL (optional)</Label><Input value={form.facebook} onChange={(e) => setForm({ ...form, facebook: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Instagram URL (optional)</Label><Input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Sort order (lower = shown first)</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="mt-1.5" /></div>
          <div className="sm:col-span-2 flex gap-2">
            <button type="submit" className="btn-teal rounded-full px-6 py-2.5 font-semibold inline-flex items-center gap-2"><Save className="h-4 w-4" />{editing ? "Save changes" : "Add staff member"}</button>
            {editing && <button type="button" onClick={() => { setEditing(null); setForm(empty); }} className="rounded-full px-6 py-2.5 border border-[#e7dfd0]">Cancel</button>}
          </div>
        </form>
      </Card>

      <div className="mt-6 space-y-3">
        {list.map((s) => (
          <Card key={s.id} className="flex items-center gap-4">
            {s.photo ? (
              <img src={s.photo} alt={s.name} className="h-16 w-16 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="h-16 w-16 rounded-full bg-[#0e5469] flex items-center justify-center flex-shrink-0">
                <UserRound className="h-6 w-6 text-[#c8a36a]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-serif-display text-lg text-[#0a2d38]">{s.name}</div>
              <div className="text-xs text-[#c8a36a] uppercase tracking-[0.24em] font-semibold">{s.role || "Staff"}</div>
              {s.email && <div className="text-xs text-[#7a8a8f] mt-1">{s.email}</div>}
            </div>
            <a href="/staff" target="_blank" rel="noreferrer" className="text-[#0e5469] hover:text-[#c8a36a]" title="View staff page"><ExternalLink className="h-4 w-4" /></a>
            <button onClick={() => edit(s)} className="text-[#0e5469] hover:text-[#c8a36a]"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => del(s.id)} className="text-red-600 hover:text-red-800"><Trash2 className="h-4 w-4" /></button>
          </Card>
        ))}
        {list.length === 0 && <div className="text-center py-10 text-[#7a8a8f]">No staff added yet. Add your first one above.</div>}
      </div>
    </div>
  );
}

